// track/components/TransactionsView.tsx
import React from 'react';
import { Upload, Plus, CreditCard } from 'lucide-react';
import { Transaction, Client, Vendor, TransactionMethod } from '../types';
import Pagination from './Pagination';

interface TransactionsViewProps {
  transactions: Transaction[];
  clients: Client[];
  vendors: Vendor[];
  transactionMethods: TransactionMethod[];
  canWrite: boolean;
  onAddTransaction: () => void;
  onAddMethod: () => void;
  onImportCSV: () => void;
  onAssignVendor: (transactionUid: string, vendorId: string) => void;
  selectedMonth: string;
  onMonthChange: (month: string) => void;
  selectedSourceFilter: string;
  onSourceFilterChange: (sourceId: string) => void;
  currentPage: number;
  onPageChange: (page: number) => void;
}

const TransactionsView: React.FC<TransactionsViewProps> = ({
  transactions,
  clients,
  vendors,
  transactionMethods,
  canWrite,
  onAddTransaction,
  onAddMethod,
  onImportCSV,
  onAssignVendor,
  selectedMonth,
  onMonthChange,
  selectedSourceFilter,
  onSourceFilterChange,
  currentPage,
  onPageChange,
}) => {
  // Helper to get names for display
  const getClientName = (clientId?: string) => clients.find(c => c.id === clientId)?.name;
  const getVendorName = (vendorId?: string) => vendors.find(v => v.id === vendorId)?.name;
  const getSourceName = (methodId?: string) => transactionMethods.find(m => m.id === methodId)?.name;

  // Generate month options for the dropdown
  const generateMonthOptions = () => {
    const options = [];
    const currentDate = new Date();
    for (let year = currentDate.getFullYear(); year >= currentDate.getFullYear() - 2; year--) {
      for (let month = 12; month >= 1; month--) {
        if (year === currentDate.getFullYear() && month > currentDate.getMonth() + 1) continue;
        const monthValue = `${String(month).padStart(2, '0')}_${year}`;
        const monthName = new Date(year, month - 1).toLocaleString('default', { month: 'long', year: 'numeric' });
        options.push({ value: monthValue, label: monthName });
      }
    }
    return options;
  };

  // Filter transactions based on the selected month and source
  const filteredTransactions = transactions.filter(t => {
      const [month, year] = selectedMonth.split('_');
      const transactionDate = new Date(t.date);
      const isMonthMatch = transactionDate.getFullYear() === parseInt(year) && transactionDate.getMonth() + 1 === parseInt(month);
      const isSourceMatch = !selectedSourceFilter || t.transactionMethodId === selectedSourceFilter;
      return isMonthMatch && isSourceMatch;
  });

  // Pagination logic
  const transactionsPerPage = 20;
  const indexOfLastTransaction = currentPage * transactionsPerPage;
  const indexOfFirstTransaction = indexOfLastTransaction - transactionsPerPage;
  const currentTransactions = filteredTransactions.slice(indexOfFirstTransaction, indexOfLastTransaction);
  const totalPages = Math.ceil(filteredTransactions.length / transactionsPerPage);

  // Logic to determine which columns to show
  const showClientColumn = currentTransactions.some(t => t.clientId);
  const showCategoryColumn = currentTransactions.some(t => t.category);
  const showReferenceColumn = currentTransactions.some(t => t.reference);

  // Summary calculations
  const totalCredits = filteredTransactions.filter(t => t.type === 'credit').reduce((sum, t) => sum + Math.abs(t.amount), 0);
  const totalDebits = filteredTransactions.filter(t => t.type === 'debit').reduce((sum, t) => sum + Math.abs(t.amount), 0);

  const handleVendorAssign = (e: React.ChangeEvent<HTMLSelectElement>, transactionUid: string) => {
    const vendorId = e.target.value;
    if (vendorId) {
      onAssignVendor(transactionUid, vendorId);
      e.target.value = "";
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-4">
          <h2 className="text-2xl font-bold text-gray-900">Transactions</h2>
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Month:</label>
            <select value={selectedMonth} onChange={(e) => onMonthChange(e.target.value)} className="border border-gray-300 rounded-md px-3 py-1 text-sm">
              {generateMonthOptions().map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </div>
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Source:</label>
            <select value={selectedSourceFilter} onChange={(e) => onSourceFilterChange(e.target.value)} className="border border-gray-300 rounded-md px-3 py-1 text-sm">
              <option value="">All Sources</option>
              {transactionMethods.map(method => <option key={method.id} value={method.id}>{method.name}</option>)}
            </select>
          </div>
        </div>
        <div className="flex space-x-3">
          {canWrite && (
            <>
              <button onClick={onImportCSV} className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700">
                <Upload className="h-4 w-4 mr-2" />
                Import CSV
              </button>
              <button onClick={onAddMethod} className="flex items-center px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50">
                <CreditCard className="h-4 w-4 mr-2" />
                Add Source
              </button>
              <button onClick={onAddTransaction} className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                <Plus className="h-4 w-4 mr-2" />
                Add Transaction
              </button>
            </>
          )}
        </div>
      </div>

      {filteredTransactions.length > 0 && (
        <div className="mb-4 flex justify-between items-center text-sm text-gray-600">
            <div>
                Showing {indexOfFirstTransaction + 1}-{Math.min(indexOfLastTransaction, filteredTransactions.length)} of {filteredTransactions.length} transactions
            </div>
            <div className="flex space-x-4">
                <span>Total Credits: £{totalCredits.toFixed(2)}</span>
                <span>Total Debits: £{totalDebits.toFixed(2)}</span>
            </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        {/* --- FIX: THE FULL TABLE IS RESTORED HERE --- */}
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
              {showClientColumn && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>}
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendor</th>
              {showCategoryColumn && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>}
              {showReferenceColumn && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reference</th>}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {currentTransactions.map((transaction) => (
              <tr key={transaction.uid} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{getSourceName(transaction.transactionMethodId) || '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{new Date(transaction.date).toLocaleDateString()}</td>
                <td className="px-6 py-4 text-sm text-gray-900">{transaction.description}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <span className={transaction.amount > 0 ? 'text-green-600' : 'text-red-600'}>
                    £{Math.abs(transaction.amount).toFixed(2)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${transaction.type === 'credit' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {transaction.type === 'credit' ? 'Credit' : 'Debit'}
                  </span>
                </td>
                {showClientColumn && (
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {transaction.clientId ? (
                      <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">{getClientName(transaction.clientId)}</span>
                    ) : (<span className="text-gray-400">-</span>)}
                  </td>
                )}
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {transaction.vendorId ? (
                    <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                      {getVendorName(transaction.vendorId)}
                    </span>
                  ) : (
                    <select
                      onChange={(e) => handleVendorAssign(e, transaction.uid)}
                      className="text-xs border border-gray-300 rounded-md px-2 py-1"
                      defaultValue=""
                    >
                      <option value="" disabled>Assign Vendor...</option>
                      {vendors.map(vendor => (
                        <option key={vendor.id} value={vendor.id}>{vendor.name}</option>
                      ))}
                    </select>
                  )}
                </td>
                {showCategoryColumn && <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{transaction.category || '-'}</td>}
                {showReferenceColumn && <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{transaction.reference || '-'}</td>}
              </tr>
            ))}
          </tbody>
        </table>
        {filteredTransactions.length === 0 && (
          <div className="text-center py-8"><p className="text-gray-500">No transactions found for the selected criteria.</p></div>
        )}
      </div>

      <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={onPageChange}/>
    </div>
  );
};

export default TransactionsView;